import { useEffect, useState, useCallback } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Trash2, Save, X } from "lucide-react";

const CATEGORIES = ["all", "home", "pricing", "faq", "legal", "blog", "announcement"];

export default function AdminCMS() {
  const [rows, setRows] = useState([]);
  const [cat, setCat] = useState("all");
  const [edit, setEdit] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const load = useCallback(async () => {
    try { const params = cat === "all" ? {} : { category: cat }; setRows((await api.get("/admin/cms", { params })).data.rows); } catch (e) {}
  }, [cat]);
  useEffect(() => { load(); }, [load]);
  const del = async (id) => {
    if (!confirm("Delete this content block?")) return;
    try { await api.delete(`/admin/cms/${id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <AdminLayout subtitle="Module 16 · CMS" title="Content Management" actions={
      <Button onClick={() => setShowNew(true)} className="rounded-sm bg-ec-emerald text-ec-ink gap-2" data-testid="cms-new"><Plus className="w-4 h-4" />New content block</Button>
    }>
      <div className="flex flex-wrap gap-px bg-white/10 mb-6" data-testid="cms-tabs">
        {CATEGORIES.map((c) => (
          <button key={c} onClick={() => setCat(c)} className={`flex-1 min-w-[100px] px-4 py-3 text-sm bg-ec-ink transition-colors capitalize ${cat === c ? "border-b-2 border-ec-emerald text-white font-bold" : "text-zinc-400 hover:bg-white/5"}`} data-testid={`cms-cat-${c}`}>{c}</button>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-4" data-testid="cms-grid">
        {rows.map((c) => (
          <Card key={c.id} className="rounded-sm border-white/10 bg-white/[0.02]" data-testid={`cms-${c.key}`}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <Badge className="bg-white/10 border-white/10 rounded-sm capitalize">{c.category}</Badge>
                <span className="font-mono-plex text-[10px] text-zinc-500">{c.key}</span>
              </div>
              <div className="font-display font-bold text-white">{c.title}</div>
              <pre className="text-xs font-mono-plex text-zinc-400 mt-2 whitespace-pre-wrap line-clamp-5 bg-black/30 p-2 rounded-sm">{c.body_md || "(empty)"}</pre>
              <div className="text-[10px] text-zinc-500 mt-2 font-mono-plex">Updated · {c.updated_at?.slice(0, 16)}</div>
              <div className="flex justify-end gap-2 mt-3">
                <button onClick={() => setEdit(c)} className="px-2 py-1 text-xs border border-white/10 rounded-sm text-white hover:bg-white/10" data-testid={`cms-edit-${c.key}`}>Edit</button>
                <button onClick={() => del(c.id)} className="p-1.5 border border-ec-error/30 text-ec-error rounded-sm hover:bg-ec-error/10" data-testid={`cms-delete-${c.key}`}><Trash2 className="w-3 h-3" /></button>
              </div>
            </CardContent>
          </Card>
        ))}
        {rows.length === 0 && <div className="md:col-span-2 py-10 text-center text-zinc-500" data-testid="cms-empty">No content in this category.</div>}
      </div>

      {(showNew || edit) && <CMSModal initial={edit} isNew={showNew && !edit} onClose={() => { setShowNew(false); setEdit(null); }} onSaved={() => { setShowNew(false); setEdit(null); load(); }} />}
    </AdminLayout>
  );
}

const EMPTY_CMS = { key: "", title: "", body_md: "", category: "home" };

function CMSModal({ initial, isNew, onClose, onSaved }) {
  const [f, setF] = useState({ ...EMPTY_CMS, ...initial });
  const save = async () => {
    try {
      if (isNew) await api.post("/admin/cms", f);
      else await api.patch(`/admin/cms/${initial.id}`, f);
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6" onClick={onClose} data-testid="cms-modal">
      <div className="bg-ec-ink border border-white/10 rounded-sm max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-display text-xl font-bold text-white">{isNew ? "New content block" : `Edit · ${initial.key}`}</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <div className="grid sm:grid-cols-[1fr_180px] gap-3">
          <div>
            <Label className="eyebrow text-zinc-500">Key</Label>
            <Input value={f.key} onChange={(e) => setF({ ...f, key: e.target.value })} disabled={!isNew} className="rounded-sm bg-white/5 border-white/10 text-white mt-2 font-mono-plex" data-testid="cms-input-key" />
          </div>
          <div>
            <Label className="eyebrow text-zinc-500">Category</Label>
            <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="rounded-sm h-10 px-3 bg-white/5 border border-white/10 text-white text-sm w-full mt-2" data-testid="cms-input-category">
              {CATEGORIES.filter((c) => c !== "all").map((c) => <option key={c} value={c} className="bg-ec-ink capitalize">{c}</option>)}
            </select>
          </div>
        </div>
        <div className="mt-3">
          <Label className="eyebrow text-zinc-500">Title</Label>
          <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="rounded-sm bg-white/5 border-white/10 text-white mt-2" data-testid="cms-input-title" />
        </div>
        <div className="mt-3">
          <Label className="eyebrow text-zinc-500">Body (Markdown)</Label>
          <textarea value={f.body_md} onChange={(e) => setF({ ...f, body_md: e.target.value })} rows={14} className="rounded-sm w-full mt-2 p-3 bg-white/5 border border-white/10 text-white text-sm font-mono-plex" data-testid="cms-input-body" />
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Button variant="outline" className="rounded-sm border-white/10 text-white" onClick={onClose}>Cancel</Button>
          <Button onClick={save} className="rounded-sm bg-ec-emerald text-ec-ink gap-2" data-testid="cms-save"><Save className="w-4 h-4" />Save</Button>
        </div>
      </div>
    </div>
  );
}
